
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Gauge, ChevronRight, Zap } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Calendar } from "@/components/ui/calendar";
import { format } from "date-fns";
import { nl } from "date-fns/locale";

interface ChiptuningOptionsProps {
  vehicleId: string;
  currentPowerHp?: number;
  brand?: string;
  model?: string;
}

const stages = [
  {
    id: "stage1",
    name: "Stage 1",
    description: "Basis optimalisatie voor maximale prestaties",
    powerGain: 25,
    price: 499,
    details: [
      "ECU Optimalisatie",
      "Verbeterde gasrespons",
      "Meer trekkracht",
    ]
  },
  {
    id: "stage2",
    name: "Stage 2",
    description: "Geavanceerde tuning met hardware aanpassingen",
    powerGain: 40,
    price: 899,
    details: [
      "Stage 1 + Extra's",
      "Sportuitlaat systeem",
      "Inlaatsysteem optimalisatie",
    ]
  },
  {
    id: "stage3",
    name: "Stage 3",
    description: "Maximale prestaties voor het circuit",
    powerGain: 60,
    price: 1499,
    details: [
      "Stage 2 + Extra's",
      "Turbo upgrades",
      "Intercooler systeem",
      "Custom mapping"
    ]
  }
];

// Beschikbare tijdsloten (in een echte applicatie zou dit van de backend komen)
const timeSlots = [
  { time: "09:00", available: true },
  { time: "09:30", available: true },
  { time: "10:00", available: true },
  { time: "10:30", available: false },
  { time: "11:00", available: true },
  { time: "11:30", available: true },
  { time: "12:00", available: false },
  { time: "13:00", available: true },
  { time: "13:30", available: true },
  { time: "14:00", available: true },
  { time: "14:30", available: true },
  { time: "15:00", available: true },
  { time: "15:30", available: true },
  { time: "16:00", available: true },
  { time: "16:30", available: true },
];

export const ChiptuningOptions = ({ vehicleId, currentPowerHp, brand, model }: ChiptuningOptionsProps) => {
  const [selectedStage, setSelectedStage] = useState<string | undefined>();
  const [showSchedule, setShowSchedule] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date>();
  const [selectedTime, setSelectedTime] = useState<string | null>(null);
  const { toast } = useToast();
  const today = new Date();

  const handleScheduleAppointment = async () => {
    if (!selectedDate || !selectedStage || !selectedTime) return;

    try {
      const { error } = await supabase
        .from("vehicle_maintenance")
        .insert({
          vehicle_id: vehicleId,
          requested_date: format(selectedDate, "yyyy-MM-dd"),
          requested_time: selectedTime,
          status: "pending",
          description: `Chiptuning ${stages.find(s => s.id === selectedStage)?.name}`,
        });

      if (error) throw error;

      toast({
        title: "Afspraak aangevraagd",
        description: "Je chiptuning afspraak is succesvol aangevraagd.",
      });

      setShowSchedule(false);
      setSelectedDate(undefined);
      setSelectedTime(null);
      setSelectedStage(undefined);
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Fout bij aanvragen",
        description: error.message,
      });
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <Gauge className="h-5 w-5 text-[#B1A06D]" />
          <CardTitle className="text-xl font-semibold">Chiptuning Opties</CardTitle>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="text-sm text-muted-foreground">
          Specifieke chiptuning mogelijkheden voor je {brand} {model}
        </div>

        <div className="grid gap-4">
          {stages.map((stage) => (
            <div
              key={stage.id}
              className="relative rounded-lg border p-4 transition-all hover:shadow-md"
            >
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-semibold">{stage.name}</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    {stage.description}
                  </p>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold">€{stage.price}</div>
                </div>
              </div>

              <div className="mt-4 space-y-2">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <Zap className="h-4 w-4 text-[#B1A06D]" />
                  <span>
                    {currentPowerHp && `${currentPowerHp} → ${currentPowerHp + stage.powerGain} PK`}
                    {!currentPowerHp && `+${stage.powerGain} PK`}
                  </span>
                </div>
                <ul className="mt-2 space-y-1">
                  {stage.details.map((detail, index) => (
                    <li key={index} className="text-sm text-muted-foreground flex items-center gap-2">
                      <ChevronRight className="h-3 w-3" />
                      {detail}
                    </li>
                  ))}
                </ul>
              </div>

              <Dialog open={showSchedule} onOpenChange={setShowSchedule}>
                <DialogTrigger asChild>
                  <Button 
                    className="w-full mt-4"
                    onClick={() => setSelectedStage(stage.id)}
                  >
                    Plan Afspraak
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Plan je chiptuning afspraak</DialogTitle>
                    <DialogDescription>
                      Kies een datum en tijd voor je {stage.name} chiptuning afspraak.
                    </DialogDescription>
                  </DialogHeader>

                  <div className="space-y-4 py-4">
                    <div className="flex flex-col sm:flex-row">
                      <Calendar
                        mode="single"
                        selected={selectedDate}
                        onSelect={setSelectedDate}
                        disabled={[{ before: today }]}
                        locale={nl}
                        className="rounded-md border"
                      />
                      <div className="w-full p-4 border-t sm:border-t-0 sm:border-l">
                        <div className="h-5 mb-3">
                          {selectedDate && (
                            <p className="text-sm font-medium">
                              {format(selectedDate, "EEEE d MMMM", { locale: nl })}
                            </p>
                          )}
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                          {timeSlots.map(({ time, available }) => (
                            <Button
                              key={time}
                              variant={selectedTime === time ? "default" : "outline"}
                              size="sm"
                              className="w-full"
                              onClick={() => setSelectedTime(time)}
                              disabled={!available}
                            >
                              {time}
                            </Button>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>

                  <Button 
                    onClick={handleScheduleAppointment}
                    disabled={!selectedDate || !selectedTime}
                    className="w-full"
                  >
                    Bevestig Afspraak
                  </Button>
                </DialogContent>
              </Dialog>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};
